/**
 * Outfit layer renderer — draws pixel-art hair / face / hat / shirt / pants
 * on top of the 64x64 character sprite. Anchor (x, y) = sprite feet
 * (same anchor drawPlayerSprite uses), coordinates in frame units (0-64).
 *
 * Base calibration = player_sheet.png (Suit): head top 13, eyes 21,
 * torso 32-47, hips 48, feet 61. Per-character offsets in CHAR_ANCHORS
 * (measured from each sheet's down-idle frame):
 *   h = head offset (hair/hats sit on the sheet's actual top, incl. baked hats)
 *   f = feet offset (face/shirt/pants anchored to the sheet's actual feet)
 */

// Measured per character sheet — h/f = head/feet y-offsets, bw = torso width ratio vs Suit
export const CHAR_ANCHORS = {
  male: { h: 0, f: 0, bw: 1 },
  base_m: { h: 0, f: 1, bw: 1 },
  base_f: { h: 0, f: 0, bw: 0.93 },
  athlete: { h: 0, f: 0, bw: 0.93 },
  cowboy: { h: 0, f: 0, bw: 0.83 },
  dj: { h: -1, f: -1, bw: 1 },
  formal: { h: 0, f: 0, bw: 0.62 },
  mage: { h: -2, f: -1, bw: 0.97 },
  ninja: { h: 0, f: 1, bw: 1 },
  pilot: { h: 0, f: 1, bw: 1.03 },
  pirate: { h: 0, f: 0, bw: 0.93 },
  punk: { h: 0, f: 0, bw: 0.83 },
  royalty: { h: -1, f: -1, bw: 1.1 },
  surfer: { h: -1, f: -1, bw: 1 },
  vampire: { h: 0, f: 0, bw: 1 },
  astronaut: { h: 0, f: 0, bw: 1 },
  knight: { h: 0, f: 0, bw: 0.93 },
  chef: { h: 0, f: 0, bw: 0.97 },
  zombie: { h: 0, f: 0, bw: 0.93 },
  alien: { h: 0, f: 0, bw: 0.83 },
  samurai: { h: 0, f: 0, bw: 0.97 },
  clown: { h: 0, f: 0, bw: 0.93 },
  devil: { h: 0, f: 0, bw: 0.93 },
  angel: { h: 0, f: 0, bw: 1.1 },
  skeleton: { h: 0, f: 0, bw: 0.97 },
  werewolf: { h: -1, f: 0, bw: 1.03 },
  boxer: { h: 0, f: 1, bw: 1.03 },
  firefighter: { h: 0, f: 0, bw: 0.97 },
  doctor: { h: 0, f: 1, bw: 1 },
  wizard: { h: 0, f: 0, bw: 0.87 },
  ballerina: { h: 0, f: 0, bw: 0.87 },
};

const shade = (hex, f) => {
  if (!hex || hex[0] !== '#') return hex;
  const n = parseInt(hex.slice(1), 16);
  const c = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
  return `rgb(${c((n >> 16) & 255)},${c((n >> 8) & 255)},${c(n & 255)})`;
};

export function drawChefHat(ctx, cx, cy, s = 1) {
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = '#f5f5f0';
  ctx.fillRect(cx - 8 * s, cy - 2 * s, 16 * s, 4 * s);
  ctx.beginPath();
  ctx.arc(cx - 5 * s, cy - 6 * s, 4.5 * s, 0, Math.PI * 2);
  ctx.arc(cx, cy - 8 * s, 5.5 * s, 0, Math.PI * 2);
  ctx.arc(cx + 5 * s, cy - 6 * s, 4.5 * s, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillRect(cx - 7 * s, cy - 7 * s, 14 * s, 6 * s);
  ctx.fillStyle = '#d8d8cf';
  ctx.fillRect(cx - 8 * s, cy + 1 * s, 16 * s, 1.2 * s);
  ctx.restore();
}

// Chef hat sitting on the 96px turtle sprite (centered at x,y)
export function drawTurtleManHat(ctx, x, y, scale = 1) {
  drawChefHat(ctx, x, y - 38 * scale, scale * 1.1);
}

// Spinning mirror ball replacing the head (Disco Head special)
export function drawDiscoHead(ctx, cx, cy, s = 1) {
  const t = Date.now() * 0.002;
  ctx.save();
  const rad = 9 * s;
  const grad = ctx.createRadialGradient(cx - rad * 0.35, cy - rad * 0.35, rad * 0.15, cx, cy, rad);
  grad.addColorStop(0, '#ffffff');
  grad.addColorStop(0.5, '#c9cdd6');
  grad.addColorStop(1, '#7d8494');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.fill();
  // mirror grid
  ctx.strokeStyle = 'rgba(60,65,80,0.55)';
  ctx.lineWidth = Math.max(1, 0.7 * s);
  ctx.beginPath();
  ctx.ellipse(cx, cy, rad, rad * 0.45, 0, 0, Math.PI * 2);
  ctx.ellipse(cx, cy, rad, rad * 0.85, 0, 0, Math.PI * 2);
  ctx.stroke();
  const spin = (t % (Math.PI * 2));
  for (let i = 0; i < 3; i++) {
    const w = Math.abs(Math.cos(spin + (i * Math.PI) / 3)) * rad;
    ctx.beginPath();
    ctx.ellipse(cx, cy, w, rad, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  // sparkles
  for (let i = 0; i < 3; i++) {
    const a = t * 1.7 + (i * Math.PI * 2) / 3;
    const sx = cx + Math.cos(a) * rad * 0.6;
    const sy = cy + Math.sin(a * 1.3) * rad * 0.5;
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.fillRect(sx - 0.8 * s, sy - 0.8 * s, 1.6 * s, 1.6 * s);
  }
  ctx.restore();
}

// Faint silhouette used behind item thumbnails in the editor
export function drawMannequin(ctx, x, y, s = 1) {
  const px = (fx) => x + (fx - 32) * s;
  const py = (fy) => y + (fy - 58) * s;
  ctx.save();
  ctx.fillStyle = 'rgba(255,255,255,0.14)';
  ctx.beginPath();
  ctx.arc(px(32), py(21), 9 * s, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillRect(px(24), py(31), 16 * s, 16 * s);
  ctx.fillRect(px(24.5), py(47), 6.2 * s, 14 * s);
  ctx.fillRect(px(33.3), py(47), 6.2 * s, 14 * s);
  ctx.restore();
}

export function drawOutfitLayers(
  ctx,
  avatar,
  x,
  y,
  direction = 'down',
  isWalking = false,
  walkPhase = 0,
  scale = 1.4,
) {
  if (!avatar) return;
  const s = scale;
  const anch = CHAR_ANCHORS[avatar?.gender] || CHAR_ANCHORS.male;
  let dy = 0; // section offset: anch.f for body items, anch.h for head items
  let wxf = 1; // horizontal fit factor: anch.bw for body items so clothes hug the torso
  const px = (fx) => x + (fx - 32) * s * wxf;
  const py = (fy) => y + (fy - 58 + dy) * s;
  const r = (fx, fy, w, h, color) => {
    ctx.fillStyle = color;
    ctx.fillRect(px(fx), py(fy), w * s * wxf, h * s);
  };
  const dot = (fx, fy, rad, color) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(px(fx), py(fy), rad * s, 0, Math.PI * 2);
    ctx.fill();
  };
  const ring = (fx, fy, rad, color, lw = 1) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, lw * s);
    ctx.beginPath();
    ctx.arc(px(fx), py(fy), rad * s, 0, Math.PI * 2);
    ctx.stroke();
  };
  const side = direction === 'left' ? -1 : direction === 'right' ? 1 : 0;
  const up = direction === 'up';
  // gait sync: the sheet walk cycle is 8 frames; walkPhase advances ~1/frame
  const cyc = walkPhase * 0.79;
  const swing = isWalking ? Math.sin(cyc) * 2.2 : 0;
  const bob = isWalking ? -Math.abs(Math.sin(cyc)) * 1.0 : Math.sin(Date.now() * 0.0022) * 0.3;
  // shaded garment block: outline, base, top highlight, hem shadow, side tone
  const panel = (fx, fy, w, h, c) => {
    r(fx - 0.5, fy - 0.5, w + 1, h + 1, shade(c, 0.45));
    r(fx, fy, w, h, c);
    r(fx, fy, w, 1.2, shade(c, 1.22));
    r(fx, fy + h - 1.4, w, 1.4, shade(c, 0.7));
    r(fx + w - 1.2, fy + 1.2, 1.2, h - 2.6, shade(c, 0.82));
  };
  // shaded trouser leg with cuff
  const leg = (lx, ly, w, len, c) => {
    if (len <= 0.5) return;
    r(lx - 0.4, ly - 0.4, w + 0.8, len + 0.8, shade(c, 0.5));
    r(lx, ly, w, len, c);
    r(lx, ly, w, 1, shade(c, 1.18));
    r(lx, ly + len - 1.6, w, 1.6, shade(c, 0.68));
    r(lx + w - 1.1, ly + 1, 1.1, Math.max(0, len - 2.6), shade(c, 0.82));
  };
  // shaded sleeve
  const sleeve = (sx, sy, w, h, c) => {
    r(sx - 0.4, sy - 0.4, w + 0.8, h + 0.8, shade(c, 0.5));
    r(sx, sy, w, h, c);
    r(sx, sy, w, 1, shade(c, 1.18));
    r(sx, sy + h - 1.2, w, 1.2, shade(c, 0.7));
  };

  ctx.save();
  ctx.imageSmoothingEnabled = false;

  // ---------- PANTS ---------- (hips ~48, feet ~61) — legs animate with the gait
  dy = anch.f + bob * 0.4;
  wxf = anch.bw || 1;
  const pants = avatar.pantsItem;
  if (pants && pants !== 'none') {
    const isCamo = pants === 'pants_camo';
    const isLava = pants === 'pants_lava';
    const pc = isCamo ? '#4d5b3a' : avatar.pantsColor || '#3b5bd6';
    const legTop = 48;
    const legLen = pants === 'pants_shorts' ? 7 : 13;
    const camoBlobs = (lx, w, ln) => {
      r(lx + 0.5, legTop + 2, w * 0.5, 2.5, '#2f3b26');
      if (ln > 8) r(lx + w * 0.35, legTop + 6.5, w * 0.55, 2.5, '#6b7a4f');
      if (ln > 11) r(lx + 0.2, legTop + 10, w * 0.45, 2, '#2f3b26');
    };
    const stepL = isWalking ? Math.max(0, Math.sin(cyc)) * 2 : 0;
    const stepR = isWalking ? Math.max(0, -Math.sin(cyc)) * 2 : 0;
    if (isLava) {
      const t = Date.now() * 0.003;
      const legs = side !== 0
        ? [[29 - swing * 0.7, 6, stepR], [29 + swing * 0.7, 6, stepL]]
        : [[24.5, 6.2, stepL], [33.3, 6.2, stepR]];
      legs.forEach(([lx, w, lift], li) => {
        const ln = legLen - lift;
        leg(lx, legTop, w, ln, '#2a1206');
        for (let i = 0; i < 3; i++) {
          const by = legTop + 1.5 + ((t * 3 + i * 4 + li * 2) % Math.max(2, ln - 2));
          r(lx + 0.8 + (i % 2) * (w / 2 - 1), by, w / 2 - 0.5, 1.8, i % 2 ? '#f97316' : '#ef4444');
        }
      });
      ctx.save();
      ctx.shadowColor = '#f97316';
      ctx.shadowBlur = 6 * s;
      r(side !== 0 ? 27.5 : 24.5, legTop - 2.6, side !== 0 ? 9 : 15, 3.2, '#3d1a08');
      ctx.restore();
      r(side !== 0 ? 27.5 : 24.5, legTop - 2.6, side !== 0 ? 9 : 15, 1.1, '#57250e');
    } else if (side !== 0) {
      // side view: back leg swings behind (darker), front leg leads
      leg(29 - swing * 0.7, legTop, 6, legLen - stepR, shade(pc, 0.66));
      leg(29 + swing * 0.7, legTop, 6, legLen - stepL, pc);
      // hip + belt
      r(27.5, legTop - 2.6, 9, 3.2, shade(pc, 0.92));
      r(27.5, legTop - 2.6, 9, 1.1, shade(pc, 0.55));
      if (isCamo) camoBlobs(29 + swing * 0.7, 6, legLen - stepL);
      if (pants === 'pants_jeans' || !['pants_shorts', 'pants_track', 'pants_cargo', 'pants_formal', 'pants_camo'].includes(pants)) {
        r(29 + swing * 0.7 + 4.6, legTop + 1, 0.8, legLen - stepL - 2.5, shade(pc, 1.35));
      }
      if (pants === 'pants_track') r(31.2 + swing * 0.7, legTop, 1.4, legLen - stepL, '#f0f0f0');
      if (pants === 'pants_cargo') {
        r(30 + swing * 0.7, legTop + 4.5, 4, 3.2, shade(pc, 0.72));
        r(30 + swing * 0.7, legTop + 4.5, 4, 1, shade(pc, 0.5));
      }
      if (pants === 'pants_formal') r(31.4 + swing * 0.7, legTop, 1.2, legLen - stepL, shade(pc, 0.68));
      if (pants === 'pants_shorts') r(29 + swing * 0.7, legTop + legLen - stepL - 1.2, 6, 1.2, shade(pc, 1.25));
    } else {
      // front/back view: alternating stride + inseam shadow
      leg(24.5, legTop, 6.2, legLen - stepL, pc);
      leg(33.3, legTop, 6.2, legLen - stepR, pc);
      r(30.7, legTop + 0.5, 2.6, Math.min(legLen - stepL, legLen - stepR) * 0.55, shade(pc, 0.58));
      // hip + belt
      r(24.5, legTop - 2.6, 15, 3.4, shade(pc, 0.92));
      r(24.5, legTop - 2.6, 15, 1.1, shade(pc, 0.55));
      if (isCamo) {
        camoBlobs(24.5, 6.2, legLen - stepL);
        camoBlobs(33.3, 6.2, legLen - stepR);
      }
      if (pants === 'pants_jeans' || !['pants_shorts', 'pants_track', 'pants_cargo', 'pants_formal', 'pants_camo'].includes(pants)) {
        r(25, legTop + 1, 0.8, legLen - stepL - 2.5, shade(pc, 1.35));
        r(38.2, legTop + 1, 0.8, legLen - stepR - 2.5, shade(pc, 1.35));
      }
      if (pants === 'pants_track') {
        r(25.6, legTop, 1.4, legLen - stepL, '#f0f0f0');
        r(37, legTop, 1.4, legLen - stepR, '#f0f0f0');
      }
      if (pants === 'pants_cargo') {
        r(25.2, legTop + 4.5, 4, 3.2, shade(pc, 0.72));
        r(34.8, legTop + 4.5, 4, 3.2, shade(pc, 0.72));
        r(25.2, legTop + 4.5, 4, 1, shade(pc, 0.5));
        r(34.8, legTop + 4.5, 4, 1, shade(pc, 0.5));
      }
      if (pants === 'pants_formal') {
        r(27, legTop, 1.2, legLen - stepL, shade(pc, 0.68));
        r(35.8, legTop, 1.2, legLen - stepR, shade(pc, 0.68));
      }
      if (pants === 'pants_shorts') {
        r(24.5, legTop + legLen - stepL - 1.2, 6.2, 1.2, shade(pc, 1.25));
        r(33.3, legTop + legLen - stepR - 1.2, 6.2, 1.2, shade(pc, 1.25));
      }
    }
  }

  // ---------- SHIRT ---------- (torso 32-47) — rides the body bob, sleeves swing with arms
  dy = anch.f + bob;
  const shirt = avatar.shirtItem;
  if (shirt && shirt !== 'none') {
    const sc = avatar.shirtColor || '#c0392b';
    const tX = side !== 0 ? 26 : 23;
    const tW = side !== 0 ? 12 : 18;
    const armY = 32.5;
    const drawSleeves = (len = 6.5, c = sc) => {
      if (side === 0) {
        sleeve(tX - 2.6, armY + swing * 0.55, 2.7, len, c);
        sleeve(tX + tW - 0.1, armY - swing * 0.55, 2.7, len, c);
      } else {
        sleeve(side > 0 ? tX + tW - 2.4 : tX - 0.3, armY + Math.abs(swing) * 0.3, 2.7, len, shade(c, 0.86));
      }
    };
    if (shirt === 'shirt_tee') {
      panel(tX, 32, tW, 15, sc);
      drawSleeves(6.5);
      if (!up && side === 0) r(tX + tW / 2 - 2.5, 32, 5, 1.6, shade(sc, 0.62)); // collar
    } else if (shirt === 'shirt_tank') {
      panel(tX + 1, 33.5, tW - 2, 13.5, sc);
      if (side === 0) {
        r(tX + 2.5, 31.5, 2, 3, sc);
        r(tX + tW - 4.5, 31.5, 2, 3, sc);
        r(tX + 3.5, 34.5, 1, 4, shade(sc, 1.2)); // scoop highlight
        r(tX + tW - 4.5, 34.5, 1, 4, shade(sc, 0.75));
      } else {
        r(side > 0 ? tX + tW - 3.5 : tX + 1.5, 31.5, 2, 3.5, shade(sc, 0.85));
      }
    } else if (shirt === 'shirt_vest') {
      panel(tX, 32, 5.5, 15, sc);
      panel(tX + tW - 5.5, 32, 5.5, 15, sc);
      r(tX + 1, 34, 1, 11, shade(sc, 1.2));
      r(tX + tW - 2, 34, 1, 11, shade(sc, 0.72));
      if (!up && side === 0) {
        dot(tX + 4.6, 38, 0.7, shade(sc, 0.5));
        dot(tX + 4.6, 42, 0.7, shade(sc, 0.5));
      }
    } else if (shirt === 'shirt_hoodie') {
      panel(tX - 1, 31, tW + 2, 16.5, sc);
      drawSleeves(9, sc);
      r(tX + 2, 41, tW - 4, 4.8, shade(sc, 0.76)); // kangaroo pocket
      r(tX + 2, 41, tW - 4, 1, shade(sc, 0.58));
      if (up) {
        r(24.5, 19, 15, 11, shade(sc, 0.85));
        r(25.5, 20, 13, 1.4, shade(sc, 0.65)); // hood fold
      } else {
        r(tX - 1, 29.5, tW + 2, 2.6, shade(sc, 0.66)); // hood bunched at neck
        if (side === 0) {
          r(29.2, 33.5, 1.2, 5 + swing * 0.4, '#e8e8e8');
          r(33.6, 33.5, 1.2, 5 - swing * 0.4, '#e8e8e8');
        }
      }
    } else if (shirt === 'shirt_jacket') {
      panel(tX, 31.5, tW, 15.5, sc);
      drawSleeves(9.5, sc);
      r(tX, 31.5, tW, 2.2, shade(sc, 0.66)); // collar band
      if (!up) {
        r(tX + tW / 2 - 1, 33.5, 2, 13.5, shade(sc, 0.5)); // open zip
        r(tX + tW / 2 - 0.4, 33.5, 0.8, 13.5, '#c9ced6'); // zipper glint
        if (side === 0) {
          r(tX + 1, 33.5, 2.4, 1.6, shade(sc, 0.72)); // lapel notches
          r(tX + tW - 3.4, 33.5, 2.4, 1.6, shade(sc, 0.72));
        }
      }
    } else if (shirt === 'shirt_neon_pulse') {
      const hue = (Date.now() * 0.08) % 360;
      const neon = `hsl(${hue}, 95%, 55%)`;
      ctx.save();
      ctx.shadowColor = neon;
      ctx.shadowBlur = 8 * s;
      r(tX, 32, tW, 15, `hsl(${hue}, 70%, 25%)`);
      ctx.restore();
      r(tX, 32, tW, 1.2, `hsl(${hue}, 70%, 38%)`);
      r(tX + 2, 34, tW - 4, 1.6, neon);
      r(tX + 2, 38, tW - 4, 1.6, neon);
      r(tX + 2, 42, tW - 4, 1.6, neon);
    } else if (shirt === 'shirt_suit') {
      const dark = '#1a1d2e';
      panel(tX, 31.5, tW, 15.5, dark);
      drawSleeves(10, dark);
      if (!up && side === 0) {
        // shirt + tie + lapels
        r(30, 33, 4, 9, '#e8e8e8');
        r(31.2, 33, 1.6, 7.5, avatar.shirtColor || '#c0392b');
        ctx.fillStyle = '#262b42';
        ctx.beginPath();
        ctx.moveTo(px(tX + 2), py(32));
        ctx.lineTo(px(30.5), py(33));
        ctx.lineTo(px(tX + 2), py(38.5));
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(px(tX + tW - 2), py(32));
        ctx.lineTo(px(33.5), py(33));
        ctx.lineTo(px(tX + tW - 2), py(38.5));
        ctx.closePath();
        ctx.fill();
        dot(32, 44, 0.7, '#c9a44a');
      } else if (!up) {
        r(side > 0 ? tX + tW - 4 : tX + 1.5, 33, 2.5, 7, '#e8e8e8');
      }
      r(tX, 31.5, tW, 1.6, '#262b42');
    }
    // hem shadow grounding the shirt onto the pants
    r(tX + 0.5, 46.4, tW - 1, 1.2, 'rgba(0,0,0,0.22)');
  }

  // ---------- FACE ---------- (eyes ~21, hidden when facing away) — tracks head bob
  wxf = 1;
  dy = anch.f + bob;
  const face = avatar.faceItem;
  if (face && face !== 'none' && !up) {
    const eyeY = 21;
    if (face === 'face_shades') {
      if (side === 0) {
        r(24, eyeY, 6.5, 3.5, '#0c0c0e');
        r(33.5, eyeY, 6.5, 3.5, '#0c0c0e');
        r(30.5, eyeY + 0.8, 3, 1.4, '#0c0c0e');
      } else {
        r(side > 0 ? 31 : 24, eyeY, 9, 3.5, '#0c0c0e');
      }
    } else if (face === 'face_round_glasses') {
      if (side === 0) {
        ring(27.5, eyeY + 1.5, 2.8, '#d8d8d8');
        ring(36.5, eyeY + 1.5, 2.8, '#d8d8d8');
        r(30, eyeY + 1, 4, 1, '#d8d8d8');
      } else {
        ring(side > 0 ? 36 : 28, eyeY + 1.5, 3, '#d8d8d8');
      }
    } else if (face === 'face_monocle') {
      const mx = side < 0 ? 28 : 36.5;
      ring(mx, eyeY + 1.5, 3, '#f4c430', 1.1);
      dot(mx + 2.5, eyeY + 5, 0.8, '#f4c430');
      dot(mx + 3.5, eyeY + 7.5, 0.8, '#f4c430');
      dot(mx + 4, eyeY + 10, 0.8, '#f4c430');
    } else if (face === 'face_warpaint') {
      const red = '#dc2626';
      if (side === 0) {
        r(23.5, eyeY + 2, 4.5, 1.6, red);
        r(23.5, eyeY + 4.6, 4.5, 1.6, red);
        r(36, eyeY + 2, 4.5, 1.6, red);
        r(36, eyeY + 4.6, 4.5, 1.6, red);
      } else {
        const cx2 = side > 0 ? 35 : 24.5;
        r(cx2, eyeY + 2, 4.5, 1.6, red);
        r(cx2, eyeY + 4.6, 4.5, 1.6, red);
      }
    } else if (face === 'face_eyepatch') {
      dot(side >= 0 ? 27.5 : 36.5, eyeY + 1.5, 3, '#0c0c0e');
      r(21, eyeY - 1.5, 22, 1.4, '#0c0c0e');
    } else if (face === 'face_ski_mask') {
      r(24, 15, 16, 15.5, '#23272e');
      if (side === 0) {
        r(26.5, eyeY, 4, 2.6, '#cfd6dd');
        r(33.5, eyeY, 4, 2.6, '#cfd6dd');
      } else {
        r(side > 0 ? 33 : 26, eyeY, 5, 2.6, '#cfd6dd');
      }
    } else if (face === 'face_rainbow_visor') {
      const hue = (Date.now() * 0.1) % 360;
      ctx.save();
      ctx.shadowColor = `hsl(${hue}, 95%, 60%)`;
      ctx.shadowBlur = 6 * s;
      if (side === 0) r(23.5, eyeY - 0.5, 17, 4.5, `hsl(${hue}, 90%, 50%)`);
      else r(side > 0 ? 30 : 24, eyeY - 0.5, 10, 4.5, `hsl(${hue}, 90%, 50%)`);
      ctx.restore();
    } else if (face === 'face_laser_eyes') {
      const pulse = 0.6 + 0.4 * Math.abs(Math.sin(Date.now() * 0.006));
      ctx.save();
      ctx.shadowColor = '#ff1a1a';
      ctx.shadowBlur = 10 * s * pulse;
      const eyes = side === 0 ? [27.5, 36.5] : [side > 0 ? 36 : 28];
      eyes.forEach((ex) => {
        dot(ex, eyeY + 1.2, 1.8, '#ff2222');
        ctx.globalAlpha = 0.55 * pulse;
        r(ex - 0.7, eyeY + 2, 1.4, 6 + 3 * pulse, '#ff4444');
        ctx.globalAlpha = 1;
      });
      ctx.restore();
    } else if (face === 'face_clown_nose') {
      dot(side === 0 ? 32 : side > 0 ? 38.5 : 25.5, 25, 2.2, '#e63946');
    } else if (face === 'face_mustache') {
      r(side === 0 ? 27 : side > 0 ? 33 : 24, 26.5, side === 0 ? 10 : 7, 2.2, '#4a352a');
    } else if (face === 'face_beard') {
      r(25, 27, 14, 4, '#4a352a');
      r(27, 30.5, 10, 2.5, '#4a352a');
    }
  }

  // ---------- HAIR ---------- (sits on the character's actual head/hat top, bobs with gait)
  dy = anch.h + bob;
  const hair = avatar.hairStyle;
  if (hair && hair !== 'none') {
    const hc = avatar.hairColor || '#2a2320';
    if (hair === 'hair_short') {
      r(23, 12, 18, 5, hc);
      r(22, 15, 3, 5, hc);
      r(39, 15, 3, 5, hc);
      if (up) r(23, 12, 18, 9, hc);
    } else if (hair === 'hair_spiky') {
      r(23, 13, 18, 4, hc);
      r(24, 9, 3, 5, hc);
      r(29, 8, 3, 6, hc);
      r(34, 9, 3, 5, hc);
      r(38.5, 10.5, 2.5, 4, hc);
    } else if (hair === 'hair_long') {
      r(23, 12, 18, 5, hc);
      r(21, 15, 4, 17, hc);
      r(39, 15, 4, 17, hc);
      if (up) r(23, 12, 18, 20, hc);
    } else if (hair === 'hair_bob') {
      r(22.5, 11.5, 19, 6, hc);
      r(21, 16, 4, 9, hc);
      r(39, 16, 4, 9, hc);
      if (up) r(22.5, 12, 19, 13, hc);
    } else if (hair === 'hair_buns') {
      r(23, 12, 18, 5, hc);
      dot(24, 11, 3, hc);
      dot(40, 11, 3, hc);
      if (up) r(23, 12, 18, 8, hc);
    } else if (hair === 'hair_curly') {
      r(23, 12.5, 18, 4.5, hc);
      dot(24.5, 12, 2.5, hc);
      dot(28.5, 10.5, 2.5, hc);
      dot(32, 10, 2.6, hc);
      dot(35.5, 10.5, 2.5, hc);
      dot(39.5, 12, 2.5, hc);
      dot(22, 15.5, 2, hc);
      dot(42, 15.5, 2, hc);
    } else if (hair === 'hair_ponytail') {
      r(23, 12, 18, 5, hc);
      if (up) {
        r(23, 12, 18, 8, hc);
        r(29.5, 18, 5, 13, hc);
      } else if (side !== 0) {
        r(side > 0 ? 22 : 38, 15, 4, 12, hc);
      } else {
        dot(32, 11.5, 2.4, hc);
      }
    } else if (hair === 'hair_mohawk') {
      if (side === 0) {
        r(29.5, 5, 5, 12, hc);
      } else {
        r(22, 6, 20, 5, hc);
        r(24, 4.5, 4, 4, hc);
        r(30, 3.5, 4, 5, hc);
        r(36, 4.5, 4, 4, hc);
      }
    } else if (hair === 'hair_afro') {
      dot(32, 14, 11, hc);
    } else if (hair === 'hair_fire') {
      const t = Date.now() * 0.01;
      [[25, 0], [29, 1.3], [32, 2.6], [35, 0.7], [39, 2]].forEach(([fx, ph], i) => {
        const fl = 4 + Math.abs(Math.sin(t + ph)) * 4;
        r(fx - 1.5, 16 - fl, 3, fl + 2, i % 2 ? '#f97316' : '#ef4444');
        r(fx - 0.7, 16 - fl * 0.55, 1.4, fl * 0.55 + 1, '#fbbf24');
      });
      r(23, 13.5, 18, 4, '#c2410c');
    }
  }

  // ---------- HAT ---------- (drawn over hair)
  const hat = avatar.hatItem;
  if (hat && hat !== 'none') {
    if (hat === 'hat_cap') {
      const cap = '#d63b3b';
      r(23, 10, 18, 6, cap);
      if (side === 0 && !up) r(23, 15.5, 18, 2.5, shade(cap, 0.75));
      else if (side !== 0) r(side > 0 ? 38 : 18, 14.5, 8, 2.5, shade(cap, 0.75));
    } else if (hat === 'hat_beanie') {
      r(23, 9.5, 18, 7, '#14b8a6');
      r(23, 15.5, 18, 2.5, shade('#14b8a6', 0.72));
      dot(32, 8.5, 2, '#e8e8e8');
    } else if (hat === 'hat_party') {
      ctx.fillStyle = '#ec4899';
      ctx.beginPath();
      ctx.moveTo(px(32), py(0));
      ctx.lineTo(px(26.5), py(12.5));
      ctx.lineTo(px(37.5), py(12.5));
      ctx.closePath();
      ctx.fill();
      dot(30.5, 8, 1, '#fde047');
      dot(33.5, 4.5, 1, '#67e8f9');
      dot(32, 0, 1.8, '#fde047');
    } else if (hat === 'hat_headphones') {
      ctx.strokeStyle = '#1c1c22';
      ctx.lineWidth = Math.max(1.5, 2.2 * s);
      ctx.beginPath();
      ctx.arc(px(32), py(17), 10.5 * s, Math.PI, 0);
      ctx.stroke();
      r(19.5, 18.5, 4.5, 7, '#e11d48');
      r(40, 18.5, 4.5, 7, '#e11d48');
    } else if (hat === 'hat_chef') {
      drawChefHat(ctx, px(32), py(11.5), s);
    } else if (hat === 'hat_cowboy') {
      r(18, 15, 28, 3, '#7a5230');
      r(24, 8, 16, 8, '#8a6239');
      r(24, 13.5, 16, 2, '#4a2f1b');
    } else if (hat === 'hat_tophat') {
      r(20, 15, 24, 2.5, '#101013');
      r(24, 2, 16, 13.5, '#101013');
      r(24, 12, 16, 2.5, '#c0392b');
    } else if (hat === 'hat_viking') {
      r(23, 9, 18, 7, '#9ca3af');
      r(23, 14.5, 18, 2, '#6b7280');
      r(19.5, 6, 3, 6, '#e8e2d0');
      r(41.5, 6, 3, 6, '#e8e2d0');
      dot(21, 5.5, 1.5, '#e8e2d0');
      dot(43, 5.5, 1.5, '#e8e2d0');
    } else if (hat === 'hat_propeller') {
      r(23, 10, 18, 6.5, '#3b82f6');
      r(27, 10, 3, 6.5, '#ef4444');
      r(34, 10, 3, 6.5, '#fde047');
      r(31.4, 6.5, 1.2, 4, '#555a63');
      const spin = Math.abs(Math.cos(Date.now() * 0.02));
      r(32 - 7 * spin, 5.2, 14 * spin, 1.6, '#fbbf24');
      dot(32, 6, 1, '#e11d48');
    } else if (hat === 'hat_halo') {
      ctx.save();
      ctx.strokeStyle = '#fde047';
      ctx.shadowColor = '#fde047';
      ctx.shadowBlur = 6 * s;
      ctx.lineWidth = Math.max(1.2, 1.6 * s);
      ctx.beginPath();
      ctx.ellipse(px(32), py(6), 8 * s, 2.4 * s, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    } else if (hat === 'hat_flame_crown') {
      const t = Date.now() * 0.012;
      r(23.5, 11, 17, 4.5, '#f4c430');
      [[26, 0], [32, 1.5], [38, 0.8]].forEach(([fx, ph], i) => {
        const fl = 3 + Math.abs(Math.sin(t + ph)) * 4;
        r(fx - 1.5, 11 - fl, 3, fl, i % 2 ? '#f97316' : '#ef4444');
        r(fx - 0.7, 11 - fl * 0.6, 1.4, fl * 0.6, '#fde047');
      });
    } else if (hat === 'hat_thundercloud') {
      const t = Date.now();
      const bob = Math.sin(t * 0.003) * 1.5;
      ctx.fillStyle = '#8b95a5';
      ctx.beginPath();
      ctx.arc(px(27), py(2 + bob), 4 * s, 0, Math.PI * 2);
      ctx.arc(px(32), py(0.5 + bob), 5 * s, 0, Math.PI * 2);
      ctx.arc(px(37), py(2 + bob), 4 * s, 0, Math.PI * 2);
      ctx.fill();
      if ((t / 600) % 3 < 0.4) {
        ctx.save();
        ctx.shadowColor = '#fde047';
        ctx.shadowBlur = 8 * s;
        ctx.fillStyle = '#fde047';
        ctx.beginPath();
        ctx.moveTo(px(32), py(4 + bob));
        ctx.lineTo(px(29.5), py(9 + bob));
        ctx.lineTo(px(32), py(9 + bob));
        ctx.lineTo(px(30), py(14 + bob));
        ctx.lineTo(px(34.5), py(8 + bob));
        ctx.lineTo(px(32.2), py(8 + bob));
        ctx.lineTo(px(34), py(4 + bob));
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
    } else if (hat === 'hat_ufo') {
      const t = Date.now();
      const bob = Math.sin(t * 0.004) * 2;
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = '#67e8f9';
      ctx.beginPath();
      ctx.moveTo(px(32), py(4 + bob));
      ctx.lineTo(px(25), py(14)); 
      ctx.lineTo(px(39), py(14));
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#9ca3af';
      ctx.beginPath();
      ctx.ellipse(px(32), py(2.5 + bob), 9 * s, 3 * s, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#67e8f9';
      ctx.beginPath();
      ctx.arc(px(32), py(-0.5 + bob), 3.2 * s, Math.PI, 0);
      ctx.fill();
      [26.5, 32, 37.5].forEach((fx, i) => {
        dot(fx, 3.2 + bob, 0.9, `hsl(${(t * 0.15 + i * 120) % 360}, 90%, 60%)`);
      });
    } else if (hat === 'hat_crown') {
      const gold = '#f4c430';
      r(23.5, 10.5, 17, 5.5, gold);
      r(23.5, 6.5, 3, 4.5, gold);
      r(30.5, 5.5, 3, 5.5, gold);
      r(37.5, 6.5, 3, 4.5, gold);
      dot(32, 13, 1.3, '#e11d48');
      dot(26.5, 13, 1, '#3b82f6');
      dot(37.5, 13, 1, '#3b82f6');
    }
  }

  // ---------- SPECIAL: DISCO HEAD ---------- (mirror ball replaces the head)
  if (avatar.special === 'special_disco_head') {
    dy = 0;
    const headCy = py(13 + anch.h + 8.5);
    drawDiscoHead(ctx, px(32), headCy, s);
  }

  // ---------- SPECIAL: RAINBOW AURA ---------- (orbiting glowing orbs)
  if (avatar.special === 'special_rainbow_aura') {
    dy = anch.f;
    const t = Date.now() * 0.0016;
    ctx.save();
    for (let i = 0; i < 8; i++) {
      const a = t + (i * Math.PI) / 4;
      const ox = Math.cos(a) * 17;
      const oy = Math.sin(a) * 8;
      const hue = (i * 45 + Date.now() * 0.06) % 360;
      ctx.shadowColor = `hsl(${hue}, 95%, 60%)`;
      ctx.shadowBlur = 7 * s;
      dot(32 + ox, 37 + oy, 1.6, `hsl(${hue}, 95%, 62%)`);
    }
    ctx.restore();
  }

  // ---------- SPECIAL: BUTTERFLY SWARM ---------- (fluttering wings)
  if (avatar.special === 'special_butterflies') {
    dy = anch.f;
    const t = Date.now();
    const colors = ['#ec4899', '#8b5cf6', '#22d3ee'];
    for (let i = 0; i < 3; i++) {
      const a = t * 0.001 + (i * Math.PI * 2) / 3;
      const bx = 32 + Math.cos(a) * 15;
      const by = 28 + Math.sin(a * 1.4) * 12;
      const flap = Math.abs(Math.sin(t * 0.012 + i)) * 2.2 + 0.5;
      ctx.fillStyle = colors[i];
      ctx.beginPath();
      ctx.ellipse(px(bx - flap), py(by), flap * s, 1.8 * s, -0.4, 0, Math.PI * 2);
      ctx.ellipse(px(bx + flap), py(by), flap * s, 1.8 * s, 0.4, 0, Math.PI * 2);
      ctx.fill();
      r(bx - 0.4, by - 1.5, 0.8, 3, '#2a2320');
    }
  }

  ctx.restore();
}
